import React from "react";
import { pdf } from "@react-pdf/renderer";
import { SwissQRBill } from "swissqrbill/svg";
import InvoicePdf, { computeTotals as computeInvoiceTotals } from "../components/InvoicePdf";
import { COMPANY_LEGAL } from "./company";

export function normalizeCountry(c) {
  // QR-Bill exige un code ISO 2 lettres. Convertit "Suisse"/"Switzerland" → "CH" si trop long.
  const map = { suisse: "CH", switzerland: "CH", france: "FR", germany: "DE", deutschland: "DE", italy: "IT", italie: "IT", autriche: "AT", austria: "AT", liechtenstein: "LI" };
  if (!c) return "CH";
  const trimmed = String(c).trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return map[trimmed.toLowerCase()] || "CH";
}

export function buildQrBillSvg(inv, settings) {
  if (!inv.creditor_iban) return null;
  try {
    // Phase 101bz (18/02/2026) — Arrondi commercial suisse sur le montant QR-Bill.
    // Sans ça, si paid_amount partiel introduit un montant à 3 décimales, la banque
    // rejette. On force l'arrondi au multiple de 0.05 CHF le plus proche.
    const swissRound = (v) => Math.round(Number(v || 0) * 20) / 20;
    // Phase 101gd (Feb 2026) — FIX CRITIQUE : le QR-Bill utilisait `inv.total_ttc`
    // qui = SOUS-TOTAL pour les factures traiteur (avec remise et/ou frais
    // additionnels). Résultat : le client scannait et payait le sous-total au lieu
    // du total final. Bug historique.
    // Correction : recalcul via `computeInvoiceTotals` (même logique que le PDF)
    // qui applique remise (%) + frais additionnels et retourne le TTC final rond.
    const t = computeInvoiceTotals(inv);
    const totalFinal = t.ttc;
    const remainingRaw = totalFinal - (inv.paid_amount || 0);
    const remaining = swissRound(remainingRaw);
    // Phase 87bk⁷ — Norme QR-Bill : `address` doit contenir UNIQUEMENT la rue.
    // Si le user a saisi un bloc multi-lignes dans creditor_address (rue + ville + tél),
    // on ne garde que la 1re ligne pour respecter la norme et éviter la concaténation moche.
    const firstLine = (s) => (s || "").split("\n")[0].trim();
    // Phase 96c — creditor.name en dur : "Maison Thaï Pongsak Phimrat" (norme SPC :
    // doit correspondre au titulaire de l'IBAN). Ignore la valeur potentiellement
    // obsolète stockée sur l'invoice pour garantir la cohérence WhatsApp.
    // Phase 98aq — Fallback aux settings si les champs créancier de la facture sont vides
    // (protège contre factures legacy créées quand company_city/postal étaient absents).
    const cAddr = firstLine(inv.creditor_address) || firstLine(settings?.company_address || "");
    const cZip = (inv.creditor_postal_code || settings?.company_postal_code || "").trim();
    const cCity = (inv.creditor_city || settings?.company_city || "").trim();
    const cCountry = normalizeCountry(inv.creditor_country || settings?.company_country || "CH");
    const data = {
      currency: "CHF",
      amount: remaining > 0.01 ? Number(remaining.toFixed(2)) : undefined,
      additionalInformation: inv.additional_info || `Facture ${inv.reference}`,
      reference: inv.qr_reference || undefined,
      creditor: {
        name: COMPANY_LEGAL,
        address: cAddr,
        zip: cZip,
        city: cCity,
        country: cCountry,
        account: inv.creditor_iban,
      },
    };
    // Phase 98at (09/07/2026) — Norme SPS 2.3 : le débiteur (UltmtDbtr) est
    // OPTIONNEL sur le QR-Bill MAIS s'il est présent, TOUS ses champs doivent
    // être remplis (name + address + zip + city + country). Sinon la banque
    // rejette la facture (« partial debtor »). Root cause du refus signalé
    // par l'utilisateur : quelques anciennes factures avaient debtor_name
    // sans debtor_city → QR-Bill invalide.
    // Solution : n'inclure `data.debtor` que si TOUT est valide. Sinon on
    // laisse une case blanche (accepté par les scanners bancaires).
    const dName = (inv.debtor_name || "").trim();
    const dAddr = firstLine(inv.debtor_address);
    const dZip = (inv.debtor_postal_code || "").trim();
    const dCity = (inv.debtor_city || "").trim();
    if (dName && dAddr && dZip && dCity) {
      data.debtor = {
        name: dName,
        address: dAddr,
        zip: dZip,
        city: dCity,
        country: normalizeCountry(inv.debtor_country),
      };
    }
    const qr = new SwissQRBill(data, { language: "FR" });
    return qr.toString();
  } catch (e) {
    console.error("QR-Bill error:", e);
    return null;
  }
}

export function svgToPngDataUrl(svgString, widthPx = 1600) {
    return new Promise((resolve, reject) => {
      // Encode SVG en base64 → data URL. Pas de blob, pas de contamination canvas.
      let svgBase64 = "";
      try {
        svgBase64 = window.btoa(unescape(encodeURIComponent(svgString)));
      } catch (e) {
        reject(e);
        return;
      }
      const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;
      const img = new window.Image();
      img.onload = () => {
        try {
          const naturalW = img.naturalWidth || 800;
          const naturalH = img.naturalHeight || 400;
          const scale = widthPx / naturalW;
          const canvas = document.createElement("canvas");
          canvas.width = widthPx;
          canvas.height = Math.round(naturalH * scale);
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = (err) => reject(new Error(`SVG image load failed: ${err?.message || err}`));
      img.src = dataUrl;
    });
  }

// PDF facture complet (avec QR-Bill si possible) — utilisé hors back-office.
export async function buildInvoicePdfBlob(inv, settings) {
  const svgStr = buildQrBillSvg(inv, settings);
  let qrPngDataUrl = null;
  if (svgStr) {
    try { qrPngDataUrl = await svgToPngDataUrl(svgStr, 1600); } catch { qrPngDataUrl = null; }
  }
  return pdf(<InvoicePdf invoice={inv} settings={settings} qrPngDataUrl={qrPngDataUrl} />).toBlob();
}
